import React, { FC } from 'react';

import {
  TextField as MuiTextField,
  styled,
  TextFieldProps,
} from '@mui/material';

import { getCountText } from '~/components/atoms/inputs/helpers/functions';
import { colors } from '~/theme/colors';

export type StyledTextFieldProps = TextFieldProps;

export type TextFieldCustomProps = TextFieldProps & {
  maxCountIndicator?: number;
  minCountWarning?: number;
  fullHeight?: string;
  hideHelperText?: boolean;
  customStyles?: Record<string, any>;
};

export const StyledTextField = styled(MuiTextField, {
  name: 'StyledTextField',
})<TextFieldCustomProps>(
  ({ theme, fullHeight, hideHelperText, customStyles }) => ({
    '& .MuiInputBase-root': {
      borderRadius: '8px',

      ...(fullHeight && {
        height: '100%',
        display: 'flex',
        alignItems: 'stretch',
      }),

      '&.MuiInputBase-colorWarning.Mui-focused + .MuiFormHelperText-root': {
        color: colors.warning_50,
      },

      '& .MuiList-root': {
        backgroundColor: theme.palette.background.paper,
      },
    },

    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      marginBottom: '8px',
      position: 'static',
      transform: 'unset',

      '.MuiFormLabel-asterisk': {
        display: 'none',
      },

      '&.Mui-error': {
        color: 'var(--color-custom-error_40)',
      },
    },

    '& .MuiInputBase-input': {
      padding: '10px 12px',
      fontSize: '14px',
      lineHeight: '20px',

      ...(fullHeight && {
        height: '100%',
        boxSizing: 'border-box',
      }),

      color: theme.palette.text.primary,

      '&::placeholder': {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },

    '& .MuiInputBase-inputSizeLarge': {
      padding: '14px 16px',
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.25px',
    },

    '& .MuiInputBase-multiline': {
      padding: '14px 16px',

      ...(fullHeight && {
        height: '100%',
        display: 'flex',
        alignItems: 'stretch',
      }),

      '& .MuiInputBase-input': {
        padding: '0px',
        fontSize: '14px',
        lineHeight: '20px',
      },

      '& textarea': fullHeight
        ? {
            height: '100% !important',
            overflowY: 'auto !important',
            resize: 'none',
            boxSizing: 'border-box',
          }
        : {},
    },

    '& .MuiOutlinedInput-root': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.action?.hover ?? '#2a2a2a'
          : 'transparent',
      color: theme.palette.text.primary,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'var(--color-custom-neutral_60)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.2)'
            : 'var(--color-custom-primary_50)',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--color-custom-primary_40)',
        borderWidth: 2,
      },
    },

    '& .MuiOutlinedInput-notchedOutline': {
      top: 0,

      '&.Mui-error': {
        borderColor: 'var(--color-custom-error_40)',
        borderWidth: 2,
      },
    },

    '& .MuiInputAdornment-root': {
      margin: 0,
    },

    '& .MuiInputBase-adornedStart': {
      padding: 0,
    },

    '& .MuiFormHelperText-root': {
      ...(hideHelperText
        ? { display: 'none' }
        : {
            fontSize: '12px',
            lineHeight: '20px',
            margin: '8px 12px 0',
            color: theme.palette.text.secondary,
          }),
    },

    '& .MuiInputAdornment-root .MuiIconButton-root': {
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.text.primary,
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 0, 0, 0.06)',
      },
    },

    '& legend': {
      display: 'none',
    },

    ...(customStyles ?? {}),
  }),
);

const TextField: FC<TextFieldCustomProps> = ({
  maxCountIndicator,
  minCountWarning,
  color,
  value,
  ...props
}) => {
  const { countText, minCountText } = getCountText(
    value as string,
    minCountWarning,
    maxCountIndicator,
  );

  const inputColor = minCountText ? 'warning' : null;

  return (
    <StyledTextField
      value={value}
      helperText={minCountText || countText}
      {...(inputColor ? { color: color || inputColor } : {})}
      {...props}
    />
  );
};

export default TextField;
